import React, { useState, useMemo } from "react";
import { Truck, ChevronDown, ChevronUp, Check, Phone, ShieldCheck } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { formatYYYYMMDDToLocaleDateString } from "@/lib/dateOnly";
import type { TruckSortOption } from "./TrucksResultsToolbar";
import type { TruckRow } from "@/pages/SearchTrucksPageV2";
import { SearchTruckDetailsPanel } from "./SearchTruckDetailsPanel";

/* ─── Sort config ────────────────────────────────────────────────── */

interface SortDef {
  label: string;
  shortLabel: string;
}

const SORT_GROUPS: { heading: string; options: { key: TruckSortOption; def: SortDef }[] }[] = [
  {
    heading: "Recommended",
    options: [
      { key: "age_newest", def: { label: "Age — Newest", shortLabel: "Age — Newest" } },
      { key: "rate_highest", def: { label: "Rate — Highest", shortLabel: "Rate — Highest" } },
      { key: "rate_lowest", def: { label: "Rate — Lowest", shortLabel: "Rate — Lowest" } },
      { key: "available_soonest", def: { label: "Available — Soonest", shortLabel: "Available — Soonest" } },
    ],
  },
  {
    heading: "Truck Details",
    options: [
      { key: "weight_capacity_highest", def: { label: "Weight — Highest", shortLabel: "Weight — Highest" } },
      { key: "weight_capacity_lowest", def: { label: "Weight — Lowest", shortLabel: "Weight — Lowest" } },
      { key: "length_longest", def: { label: "Length — Longest", shortLabel: "Length — Longest" } },
      { key: "length_shortest", def: { label: "Length — Shortest", shortLabel: "Length — Shortest" } },
    ],
  },
  {
    heading: "Location",
    options: [
      { key: "origin_az", def: { label: "Origin — A→Z", shortLabel: "Origin — A→Z" } },
      { key: "origin_za", def: { label: "Origin — Z→A", shortLabel: "Origin — Z→A" } },
      { key: "dest_az", def: { label: "Destination — A→Z", shortLabel: "Dest — A→Z" } },
      { key: "dest_za", def: { label: "Destination — Z→A", shortLabel: "Dest — Z→A" } },
    ],
  },
  {
    heading: "Source",
    options: [
      { key: "company_az", def: { label: "Company — A→Z", shortLabel: "Company — A→Z" } },
      { key: "company_za", def: { label: "Company — Z→A", shortLabel: "Company — Z→A" } },
    ],
  },
];

const SORT_LABEL_MAP: Record<TruckSortOption, string> = {} as any;
for (const g of SORT_GROUPS) for (const o of g.options) SORT_LABEL_MAP[o.key] = o.def.shortLabel;

/* ─── Equipment map ──────────────────────────────────────────────── */

const eqMap: Record<string, string> = {
  van: "Van", reefer: "Reefer", flatbed: "Flatbed",
  step_deck: "Step Deck", power_only: "Power Only",
  box_truck: "Box Truck", hotshot: "Hotshot", other: "Other",
};

/* ─── Age helpers ────────────────────────────────────────────────── */

function formatAge(createdAt: string): { text: string; mins: number } {
  const mins = Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000);
  if (mins < 60) return { text: `${mins}m ago`, mins };
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return { text: m > 0 ? `${h}h ${m}m` : `${h}h ago`, mins };
}

function getAgeBg(mins: number): string {
  if (mins <= 20) return "bg-primary/10";
  if (mins <= 35) return "bg-muted";
  if (mins <= 90) return "bg-amber-500/10";
  return "bg-red-500/10";
}

function getAgeText(mins: number): string {
  if (mins <= 20) return "text-primary";
  if (mins <= 35) return "text-muted-foreground";
  if (mins <= 90) return "text-amber-500";
  return "text-red-500";
}

/* ─── Sortable header mapping ────────────────────────────────────── */

type SortPair = [TruckSortOption, TruckSortOption];

const HEADER_SORT_MAP: Record<string, SortPair> = {
  age: ["age_newest", "available_latest"],
  available: ["available_soonest", "available_latest"],
  origin: ["origin_az", "origin_za"],
  dest: ["dest_az", "dest_za"],
  rate: ["rate_highest", "rate_lowest"],
  length: ["length_longest", "length_shortest"],
  weight: ["weight_capacity_highest", "weight_capacity_lowest"],
  company: ["company_az", "company_za"],
};

/* ─── Column header shorthand ────────────────────────────────────── */

const TH = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <TableHead className={cn("px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground whitespace-nowrap", className)}>
    {children}
  </TableHead>
);

/* ─── Sortable column header ─────────────────────────────────────── */

interface SortableTHProps {
  children: React.ReactNode;
  className?: string;
  sortKey: string;
  currentSort: TruckSortOption;
  onSort: (sort: TruckSortOption) => void;
}

const SortableTH = ({ children, className, sortKey, currentSort, onSort }: SortableTHProps) => {
  const pair = HEADER_SORT_MAP[sortKey];
  if (!pair) return <TH className={className}>{children}</TH>;

  const [primary, reverse] = pair;
  const isActive = currentSort === primary || currentSort === reverse;
  const isReversed = currentSort === reverse;

  const handleClick = () => {
    if (!isActive) onSort(primary);
    else onSort(isReversed ? primary : reverse);
  };

  return (
    <TableHead
      className={cn(
        "px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap cursor-pointer select-none transition-colors",
        isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground/70",
        className
      )}
      onClick={handleClick}
    >
      <span className="inline-flex items-center gap-0.5">
        {children}
        {isActive && (
          isReversed
            ? <ChevronUp className="h-2.5 w-2.5" />
            : <ChevronDown className="h-2.5 w-2.5" />
        )}
      </span>
    </TableHead>
  );
};

/* ─── Props ──────────────────────────────────────────────────────── */

interface SearchTrucksResultsTableProps {
  trucks: TruckRow[];
  isLoading?: boolean;
  sortBy?: TruckSortOption;
  onSortChange?: (sort: TruckSortOption) => void;
}

/* ═══════════════════════════════════════════════════════════════════ */
export function SearchTrucksResultsTable({
  trucks,
  isLoading,
  sortBy = "age_newest",
  onSortChange,
}: SearchTrucksResultsTableProps) {

  /* ── Sorting ────────────────────────────────────────────────── */
  const sortedTrucks = useMemo(() => {
    const arr = [...trucks];
    const cmp = (a: TruckRow, b: TruckRow): number => {
      switch (sortBy) {
        case "age_newest":
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case "rate_highest":
          return (b.rate_per_mile ?? 0) - (a.rate_per_mile ?? 0);
        case "rate_lowest":
          return (a.rate_per_mile ?? 0) - (b.rate_per_mile ?? 0);
        case "available_soonest":
          return (a.available_from || "9999").localeCompare(b.available_from || "9999");
        case "available_latest":
          return (b.available_from || "0000").localeCompare(a.available_from || "0000");
        case "weight_capacity_highest":
          return (b.weight_capacity ?? 0) - (a.weight_capacity ?? 0);
        case "weight_capacity_lowest":
          return (a.weight_capacity ?? 0) - (b.weight_capacity ?? 0);
        case "length_longest":
          return (b.length_ft ?? 0) - (a.length_ft ?? 0);
        case "length_shortest":
          return (a.length_ft ?? 0) - (b.length_ft ?? 0);
        case "origin_az":
          return (a.origin_location || "").localeCompare(b.origin_location || "");
        case "origin_za":
          return (b.origin_location || "").localeCompare(a.origin_location || "");
        case "dest_az":
          return (a.destination_location || "").localeCompare(b.destination_location || "");
        case "dest_za":
          return (b.destination_location || "").localeCompare(a.destination_location || "");
        case "company_az":
          return (a.company_name || "").localeCompare(b.company_name || "");
        case "company_za":
          return (b.company_name || "").localeCompare(a.company_name || "");
        default:
          return 0;
      }
    };
    arr.sort(cmp);
    return arr;
  }, [trucks, sortBy]);

  /* ── Sort popover state ─────────────────────────────────────── */
  const [sortOpen, setSortOpen] = useState(false);

  /* ── Expansion state ────────────────────────────────────────── */
  const [expandedTruckId, setExpandedTruckId] = useState<string | null>(null);

  /* ── Show skeleton when loading with no existing results ─── */
  const showSkeleton = isLoading && trucks.length === 0;

  /* ── Empty state ─────────────────────────────────────────────── */
  if (!isLoading && trucks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
        <Truck className="h-8 w-8 opacity-30" />
        <span className="text-sm">No trucks found. Adjust your search filters.</span>
      </div>
    );
  }

  const COL_SPAN = 9;

  return (
    <div className="data-table rounded-lg border border-border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-sm)" }}>
      <style>{`
        @keyframes indeterminate {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(100%); }
        }
        .col-sep { position: relative; }
        .col-sep::after {
          content: '';
          position: absolute;
          right: 0;
          top: 20%;
          height: 60%;
          width: 1px;
          background: hsl(var(--border));
          opacity: 0.8;
        }
      `}</style>

      {/* ── Toolbar ── */}
      <div className="px-3 py-px border-b bg-muted/20 flex items-center gap-3">
        <span className="text-xs font-semibold text-muted-foreground">
          {showSkeleton ? "Searching…" : `${sortedTrucks.length} Results`}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Sort:</span>
          <Popover open={sortOpen} onOpenChange={setSortOpen}>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1.5 text-xs font-medium text-foreground bg-muted/40 hover:bg-muted border border-border rounded-md px-2.5 py-1 transition-colors">
                {SORT_LABEL_MAP[sortBy] ?? "Age — Newest"}
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start" sideOffset={4}>
              <Command>
                <CommandList className="max-h-[360px]">
                  {SORT_GROUPS.map((group) => (
                    <CommandGroup key={group.heading} heading={group.heading}>
                      {group.options.map(({ key, def }) => (
                        <CommandItem
                          key={key}
                          value={key}
                          onSelect={() => {
                            onSortChange?.(key);
                            setSortOpen(false);
                          }}
                          className="text-xs cursor-pointer"
                        >
                          <Check className={cn("mr-2 h-3 w-3", sortBy === key ? "opacity-100" : "opacity-0")} />
                          {def.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* ── Progress bar ── */}
      {isLoading && (
        <div className="h-0.5 w-full overflow-hidden">
          <Progress value={75} className="h-0.5 rounded-none [&>div]:animate-[indeterminate_1.5s_ease-in-out_infinite]" />
        </div>
      )}
      {!isLoading && <div className="h-0.5" />}

      {/* ── Mobile card list (presentation only — same data, same expand handler) ── */}
      <div className="md:hidden flex flex-col gap-2 p-3">
        {sortedTrucks.map((truck) => {
          const ageMins = Math.floor((Date.now() - new Date(truck.created_at).getTime()) / 60000);
          const ageText = ageMins < 60 ? `${ageMins}m ago` : ageMins < 1440 ? `${Math.floor(ageMins / 60)}h ago` : `${Math.floor(ageMins / 1440)}d ago`;
          const eqLabel = (truck.equipment_type || "").replace(/_/g, " ");
          const dest = truck.destination_location || "Anywhere";
          const isExpanded = expandedTruckId === truck.id;
          return (
            <React.Fragment key={`m-${truck.id}`}>
            <button
              type="button"
              onClick={() => setExpandedTruckId(isExpanded ? null : truck.id)}
              className={cn(
                "text-left rounded-lg border border-border bg-card p-3 min-w-0",
                "active:bg-muted focus:outline-none focus:ring-2 focus:ring-brand-teal-deep",
                isExpanded && "border-l-2 border-l-brand-teal bg-brand-teal-subtle/30",
              )}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <span className="text-sm font-semibold truncate min-w-0">
                  {truck.origin_location} → {dest}
                </span>
                <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                  {ageText}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="truncate min-w-0 capitalize">
                  {eqLabel}
                  {truck.available_from && <> · {truck.available_from}</>}
                </span>
                <span className="font-semibold text-foreground shrink-0 truncate max-w-[40%] inline-flex items-center gap-1">
                  {truck.documents_verified_at ? (
                    <ShieldCheck className="h-3 w-3 text-success shrink-0" />
                  ) : (
                    <span className="text-[9px] uppercase tracking-wide text-muted-foreground/70">Unverified</span>
                  )}
                  <span className="truncate">{truck.company_name || "—"}</span>
                </span>
              </div>
            </button>
            {isExpanded && (
              <div className="rounded-lg border border-border bg-background overflow-hidden">
                <SearchTruckDetailsPanel truck={truck} />
              </div>
            )}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Desktop table (≥ md) — unchanged markup, just gated ── */}
      <div className="hidden md:block">
      <Table>
        <TableHeader className="sticky top-0 z-10">
          <TableRow className="bg-muted/40 hover:bg-muted/40 border-b border-border">
            <SortableTH className="w-[88px]" sortKey="age" currentSort={sortBy} onSort={onSortChange!}>Age</SortableTH>
            <SortableTH className="min-w-[72px]" sortKey="available" currentSort={sortBy} onSort={onSortChange!}>Available</SortableTH>
            <SortableTH className="min-w-[120px] col-sep" sortKey="origin" currentSort={sortBy} onSort={onSortChange!}>Origin</SortableTH>
            <SortableTH className="min-w-[120px] col-sep" sortKey="dest" currentSort={sortBy} onSort={onSortChange!}>Dest</SortableTH>
            <TH className="text-center w-[60px]">EQ</TH>
            <SortableTH className="text-right w-[56px]" sortKey="length" currentSort={sortBy} onSort={onSortChange!}>Len</SortableTH>
            <SortableTH className="text-right min-w-[64px] col-sep" sortKey="weight" currentSort={sortBy} onSort={onSortChange!}>WT</SortableTH>
            <SortableTH className="max-w-[120px] col-sep" sortKey="company" currentSort={sortBy} onSort={onSortChange!}>Company</SortableTH>
            <TH className="max-w-[100px]">Contact</TH>
          </TableRow>
        </TableHeader>
        <TableBody>
          {showSkeleton && Array.from({ length: 8 }).map((_, i) => (
            <TableRow key={`skel-${i}`} className={cn(i % 2 === 0 ? "bg-background" : "bg-slate-50")}>
              <TableCell className="px-2.5 py-1"><Skeleton className="h-5 w-14 rounded-md" /></TableCell>
              <TableCell className="px-2.5 py-1"><Skeleton className="h-4 w-16 rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 col-sep"><Skeleton className="h-4 w-24 rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 col-sep"><Skeleton className="h-4 w-24 rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 text-center"><Skeleton className="h-4 w-10 mx-auto rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 text-right"><Skeleton className="h-4 w-8 ml-auto rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 text-right col-sep"><Skeleton className="h-4 w-12 ml-auto rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 col-sep"><Skeleton className="h-4 w-20 rounded" /></TableCell>
              <TableCell className="px-2.5 py-1"><Skeleton className="h-4 w-16 rounded" /></TableCell>
            </TableRow>
          ))}
          {!showSkeleton && sortedTrucks.map((truck, i) => {
            const age = formatAge(truck.created_at);
            const isExpanded = expandedTruckId === truck.id;
            return (
              <React.Fragment key={truck.id}>
                <TableRow
                  onClick={() => setExpandedTruckId(isExpanded ? null : truck.id)}
                  className={cn(
                    "group transition-colors duration-200 cursor-pointer",
                    "hover:bg-brand-teal/20",
                    i % 2 === 0 ? "bg-background" : "bg-slate-50",
                    isExpanded && "border-l-2 border-l-brand-teal bg-brand-teal-subtle/30",
                  )}
                >
                  {/* Age */}
                  <TableCell className="px-2.5 py-1">
                    <div className={cn("flex items-center justify-center rounded-md px-2.5 py-1", getAgeBg(age.mins), getAgeText(age.mins))}>
                      <span className="text-xs font-semibold whitespace-nowrap">{age.text}</span>
                    </div>
                  </TableCell>

                  {/* Available */}
                  <TableCell className="px-2.5 py-1 text-[13px] text-foreground whitespace-nowrap">
                    {truck.available_from ? formatYYYYMMDDToLocaleDateString(truck.available_from, "en-US", { month: "short", day: "numeric" }) : "—"}
                  </TableCell>

                  {/* Origin */}
                  <TableCell className="px-2.5 py-1 whitespace-nowrap col-sep">
                    <span className="text-sm font-medium text-foreground">{truck.origin_location || "—"}</span>
                  </TableCell>

                  {/* Destination */}
                  <TableCell className="px-2.5 py-1 whitespace-nowrap col-sep">
                    {truck.destination_location ? (
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground/60">→</span>
                        <span className="text-sm font-medium text-foreground">{truck.destination_location}</span>
                      </div>
                    ) : (
                      <span className="text-[13px] text-muted-foreground">Anywhere</span>
                    )}
                  </TableCell>

                  {/* EQ */}
                  <TableCell className="text-center px-2 py-1">
                    <span className="text-[12px] font-semibold text-muted-foreground">{eqMap[truck.equipment_type] || truck.equipment_type}</span>
                  </TableCell>

                  {/* Length */}
                  <TableCell className="text-right px-2 py-1">
                    <span className="text-[12px] text-muted-foreground font-mono">
                      {truck.length_ft ? `${truck.length_ft} ft` : "—"}
                    </span>
                  </TableCell>

                  {/* Weight */}
                  <TableCell className="text-right px-2.5 py-1 whitespace-nowrap col-sep">
                    <span className="text-[13px] text-foreground font-mono">
                      {truck.weight_capacity ? `${truck.weight_capacity.toLocaleString()} lbs` : "—"}
                    </span>
                  </TableCell>

                  {/* Company */}
                  <TableCell className="px-2.5 py-1 col-sep">
                    <div className="flex items-center gap-1 max-w-[140px]">
                      {truck.documents_verified_at ? (
                        <ShieldCheck className="h-3 w-3 text-success shrink-0" aria-label="Verified" />
                      ) : null}
                      <span className="text-[13px] font-semibold text-brand-teal-deep cursor-pointer hover:underline truncate inline-block">
                        {truck.company_name || "—"}
                      </span>
                      {!truck.documents_verified_at && (
                        <span className="text-[9px] uppercase tracking-wide text-muted-foreground/70 shrink-0">Unverified</span>
                      )}
                    </div>
                  </TableCell>

                  {/* Contact */}
                  <TableCell className="px-2 py-1">
                    {truck.contact_info ? (
                      <div className="flex items-center gap-1">
                        <Phone className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[12px] text-muted-foreground truncate max-w-[100px] inline-block">{truck.contact_info}</span>
                      </div>
                    ) : (
                      <span className="text-[12px] text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>

                {/* ── Expanded detail row ── */}
                {isExpanded && (
                  <TableRow className="bg-transparent hover:bg-transparent">
                    <TableCell colSpan={COL_SPAN} className="p-0">
                      <SearchTruckDetailsPanel truck={truck} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}
